/**
 * Image Optimization Utilities
 * 
 * Converts PNG mockup images to WebP format for better compression
 * while maintaining transparency support.
 */

import React from 'react';

/**
 * Converts image path to WebP format if it's a PNG mockup
 * Returns both WebP and fallback paths
 */
export function getOptimizedImagePath(originalPath: string): { webp: string; fallback: string } {
  // Check if it's a PNG mockup file or product-default-back.png
  if (originalPath.includes('-mockup.png') || originalPath === '/images/products/product-default-back.png') {
    const webpPath = originalPath.replace(/\.png$/i, '.webp');
    return { webp: webpPath, fallback: originalPath };
  }
  // Return original for non-PNG or non-mockup files
  return { webp: originalPath, fallback: originalPath };
}

/**
 * React component for optimized image with WebP support
 * Use this for simple img tags that need WebP optimization
 */
export function OptimizedImage({ 
  src, 
  alt, 
  className = "",
  ...props 
}: React.ImgHTMLAttributes<HTMLImageElement> & { src: string; alt: string }) {
  const { webp, fallback } = getOptimizedImagePath(src);
  const [webpFailed, setWebpFailed] = React.useState(false);
  const imageSrc = webpFailed || webp === fallback ? fallback : webp;

  return (
    <picture>
      {webp !== fallback && !webpFailed && (
        <source srcSet={webp} type="image/webp" />
      )}
      <img
        src={imageSrc}
        alt={alt}
        className={className}
        onError={(e) => {
          // If WebP fails to load, fallback to PNG
          if (!webpFailed && webp !== fallback) {
            setWebpFailed(true);
            // Force reload with fallback
            const img = e.currentTarget;
            img.src = fallback;
          }
        }}
        {...props}
      />
    </picture>
  );
}

